import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  AdornedInput,
  Anchor,
  Box,
  Combobox,
  Form,
  FormField,
  FormHelp,
  Icon,
  Input,
  Label,
  Select,
  Textarea,
} from '@zillow/constellation-10';
import { IconHammerFilled } from '@zillow/constellation-icons';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { useRentalManagerAddressAutocompleteWebApiHandler_autocomplete } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useRentalManagerUser, useRmwPaths, useSubmitSupportRequest } from '../../hooks';
import { LoadingConstants } from '../../constants';

import type { ComboboxOptionType } from '@zillow/constellation-10';
import {
  type PartialMessageTextAndUrlDto,
  type RentalManagerAddressAutocompleteWebComponentDto,
  type RentalManagerAuthCheckUserInfoWebApiDto,
  RentalManagerSupportHelpCategory,
} from '@zg-rentals/ts-rental-manager-api-client';
import type { FormStateType, HelpTopicsResponse } from './SubmitRequestComponent';

type SubmitRequestFormProps = {
  onSubmit?: () => void;
  submitClicked: boolean;
  setCanSubmit: (val: boolean) => void;
  setRequestState: (loadingState: LoadingConstants) => void;
  helpTopics: HelpTopicsResponse;
  formState: FormStateType;
  setFormState: (state: FormStateType) => void;
  setErrorAlert: (err: string) => void;
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
  queryObject?: {
    categoryKey: string | null;
    topicKey: string | null;
    listingAddress: string | null;
    unit: string | null;
    listingAlias: string | null;
  };
  zrmSignInPage?: string;
};

export const SubmitRequestForm = ({
  onSubmit = () => {},
  setRequestState,
  setCanSubmit,
  setFormState,
  setErrorAlert,
  formState,
  helpTopics,
  submitClicked,
  zrmwUser,
  queryObject,
  zrmSignInPage,
}: SubmitRequestFormProps) => {
  const paths = useRmwPaths();
  const nonZrmwUser = useRentalManagerUser().user;
  const user = zrmwUser || nonZrmwUser;

  const [email, setEmail] = useState(user.email);
  const [listing, setListing] = useState<RentalManagerAddressAutocompleteWebComponentDto>();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);

  const { loggedIn } = user;
  const { category: selectedHelpCategory, topic: selectedHelpTopic, description, unit, typedAddress } = formState;

  const { data: autocompleteData, isLoading: isAutocompleteLoading } =
    useRentalManagerAddressAutocompleteWebApiHandler_autocomplete(
      { partialAddress: typedAddress },
      { enabled: typedAddress.length >= 7 },
    );

  const { submit: submitSupportRequest } = useSubmitSupportRequest(loggedIn);
  const hasSubmittedRef = useRef(false);

  const addressOptions = autocompleteData?.addresses ?? [];
  const isAddressRequired = selectedHelpCategory?.key === RentalManagerSupportHelpCategory.MY_LISTING;
  const helpCategoryWasChosen = selectedHelpCategory !== undefined;

  const canSubmit = useMemo<boolean>(() => {
    if (isAddressRequired && ((listing === undefined && typedAddress === '') || !loggedIn)) return false;
    return description !== '' && Boolean(email) && helpCategoryWasChosen && selectedHelpTopic != null;
  }, [
    isAddressRequired,
    listing,
    typedAddress,
    loggedIn,
    description,
    email,
    helpCategoryWasChosen,
    selectedHelpTopic,
  ]);

  useEffect(() => {
    setCanSubmit(canSubmit);
  }, [canSubmit, setCanSubmit]);

  useEffect(() => {
    if (!submitClicked) {
      hasSubmittedRef.current = false;
      return;
    }
    if (!canSubmit || hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    const addressWithUnit =
      unit && typedAddress
        ? (() => {
            const parts = typedAddress.split(',');
            parts[0] += ` #${unit}`;
            return parts.join(',');
          })()
        : typedAddress;
    submitSupportRequest(
      {
        topicKey: selectedHelpTopic?.key ?? '',
        topicDescription: selectedHelpTopic?.description ?? '',
        categoryKey: selectedHelpCategory?.key ?? '',
        description,
        email,
        firstName,
        lastName,
        listingAddress: addressWithUnit,
        listingAlias: queryObject?.listingAlias,
        unit,
      },
      {
        onSuccess: () => {
          setRequestState(LoadingConstants.SUCCESS);
          onSubmit();
        },
        onError: (errorMessage: string) => {
          setRequestState(LoadingConstants.ERROR);
          setErrorAlert(errorMessage);
        },
      },
    );
  }, [
    submitClicked,
    canSubmit,
    selectedHelpTopic?.key,
    selectedHelpTopic?.description,
    selectedHelpCategory?.key,
    description,
    email,
    firstName,
    lastName,
    typedAddress,
    unit,
    queryObject?.listingAlias,
    submitSupportRequest,
    setRequestState,
    onSubmit,
    setErrorAlert,
  ]);

  const handleSelectAddress = (option: ComboboxOptionType) => {
    if (typeof option === 'object' && option !== null && 'name' in option) {
      const selectedListing = option as RentalManagerAddressAutocompleteWebComponentDto & {
        value: string;
        label: string;
      };
      setListing(selectedListing);
      setFormState({ ...formState, typedAddress: selectedListing.name });
    }
  };

  const handleSelectCategory = (value: string) => {
    const selectedCategory = helpTopics?.categories?.find((category) => category.key === value);
    if (selectedCategory) {
      setFormState({
        ...formState,
        topic: undefined,
        category: selectedCategory,
      });
    }
  };

  const handleSelectTopic = (value: string) => {
    const selectedTopic = selectedHelpCategory?.topics?.find((topic) => topic.key === value);
    if (selectedTopic) {
      setFormState({ ...formState, topic: selectedTopic });
    }
  };

  return (
    <Form data-testid="SubmitRequestForm" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
      <FormField
        readOnly={!!queryObject?.categoryKey}
        disabled={helpCategoryWasChosen}
        label={<Label>{queryObject?.categoryKey ? 'Category' : 'I need help with'}</Label>}
        control={
          <Select
            aria-label="RequestType"
            name="requestType"
            value={selectedHelpCategory?.key}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelectCategory(e.target.value)}
          >
            <option aria-label="Please select an option">-</option>

            {helpTopics?.categories?.map((category) => {
              return (
                <option key={category?.key} value={category?.key} aria-label={category?.description}>
                  {category?.description}
                </option>
              );
            })}
          </Select>
        }
        description={
          isAddressRequired && !loggedIn ? (
            <FormHelp>
              <span>
                Please{' '}
                {paths || zrmSignInPage ? (
                  <Anchor href={zrmSignInPage || paths.zrmSignInPage(`${paths.submitRequest}`)}>
                    <strong>sign in</strong>
                  </Anchor>
                ) : (
                  'sign in'
                )}{' '}
                to submit a request about your listing. If you are unable to sign in, select <strong>My Account</strong>
                .
              </span>
            </FormHelp>
          ) : null
        }
        required={true}
      />
      {helpCategoryWasChosen &&
        helpTopics?.outages?.map((outage: Array<PartialMessageTextAndUrlDto>, i: number) => {
          return (
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: { base: 'flex-start', lg: 'center' },
                justifyContent: 'flex-start',
                paddingBlock: '200',
              }}
              key={`outages-content-${i}`}
            >
              <Icon render={<IconHammerFilled />} size="md" css={{ marginRight: '100' }} />
              {<PartialMessage partialMessages={outage} fontType="body-xs" />}
            </Box>
          );
        })}
      {helpCategoryWasChosen && (
        <>
          <FormField
            readOnly={!!queryObject?.topicKey}
            disabled={Boolean(selectedHelpTopic)}
            label={<Label>{'Select the topic below that best fits your problem'}</Label>}
            control={
              <Select
                aria-label="RequestType"
                name="requestType"
                value={selectedHelpTopic?.key}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelectTopic(e.target.value)}
              >
                <option aria-label="Please select an option">-</option>

                {selectedHelpCategory?.topics?.map((topic) => {
                  return (
                    <option key={topic?.key} value={topic?.key} aria-label={topic?.description}>
                      {topic?.description}
                    </option>
                  );
                })}
              </Select>
            }
            required={true}
          />
          <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
            <FormField
              css={{ marginRight: { base: 0, lg: '400' }, width: '50%' }}
              label={<Label>First name</Label>}
              control={
                <Input
                  value={firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                />
              }
            />
            <FormField
              css={{ marginBottom: '400', width: '50%' }}
              label={<Label>Last name</Label>}
              control={
                <Input
                  value={lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                />
              }
            />
          </Box>
          <FormField
            label={<Label>Email address</Label>}
            control={
              <Input value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} />
            }
            required={true}
          />
          <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' } }}>
            <Box css={{ flexBasis: '70%' }}>
              <FormField required={isAddressRequired} css={{ marginRight: { base: 0, lg: '400' } }}>
                <Label id="street-address-label">Listing address</Label>
                <Combobox
                  appearance="input"
                  loading={isAutocompleteLoading}
                  options={
                    addressOptions?.map((option) => {
                      return {
                        ...option,
                        value: option.name,
                        label: option.name,
                      };
                    }) || []
                  }
                  value={typedAddress}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      setFormState({ ...formState, typedAddress: value });
                    }
                  }}
                  onOptionSelect={handleSelectAddress}
                  freeForm
                  showLabelForValue
                  editable={!queryObject?.listingAddress}
                />
              </FormField>
            </Box>
            <Box css={{ marginBlockEnd: '400', flexBasis: '30%' }}>
              <FormField
                label={<Label>Unit number</Label>}
                control={
                  <AdornedInput
                    startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                    input={
                      <AdornedInput.Input
                        aria-labelledby="unit-number-label"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormState({ ...formState, unit: e.target.value })
                        }
                        value={unit}
                        readOnly={!!queryObject?.unit}
                      />
                    }
                  />
                }
              />
            </Box>
          </Box>
          <FormField
            label={<Label>Description</Label>}
            control={
              <Textarea
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormState({ ...formState, description: e.target.value })
                }
                placeholder={
                  selectedHelpTopic?.descriptionFieldPlaceholder || 'Describe your problem to help us better assist you'
                }
              />
            }
            required={true}
          />
        </>
      )}
    </Form>
  );
};
